// OrganisationsPage.js
import React, { useEffect, useState, useCallback } from "react";
import { superAdmin } from "../../api";
import {
  PageHeader, Btn, Badge, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle, selectStyle,
  TempPasswordBox,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";
import ManageOrgAdminsModal from "./ManageOrgAdminsModal";

export default function OrganisationsPage() {
  const [orgs,        setOrgs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [showCreate,  setShowCreate]  = useState(false);
  const [editModal,   setEditModal]   = useState(null);
  const [confirm,     setConfirm]     = useState(null);
  const [actionLoad,  setActionLoad]  = useState(false);
  const [result,      setResult]      = useState(null);
  const [manageAdminsModal, setManageAdminsModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    superAdmin.listOrgs()
      .then(r => setOrgs(r.data?.results || r.data || []))
      .catch(() => toast.error("Failed to load organisations."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = orgs.filter(o =>
    !search || o.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleAction = async () => {
    if (!confirm) return;
    setActionLoad(true);
    try {
      if (confirm.type === "suspend") await superAdmin.suspendOrg(confirm.org.id);
      else                             await superAdmin.activateOrg(confirm.org.id);
      toast.success(`${confirm.org.name} ${confirm.type === "suspend" ? "suspended" : "activated"}.`);
      load();
    } catch { toast.error("Action failed."); }
    finally  { setActionLoad(false); setConfirm(null); }
  };

  return (
    <div style={styles.root}>
      <PageHeader
        title="Organisations"
        subtitle={`${orgs.length} organisation${orgs.length !== 1 ? "s" : ""} on platform`}
        action={<Btn size="lg" onClick={() => setShowCreate(true)}>+ New Organisation</Btn>}
      />

      <div style={styles.toolbar}>
        <input style={styles.search} placeholder="Search organisations…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
        <EmptyState icon="🏛️" title={search ? "No organisations found" : "No organisations yet"}
          subtitle={search ? "Try a different search." : "Create your first organisation."}
          action={!search && <Btn onClick={() => setShowCreate(true)}>+ Create Organisation</Btn>}
        />
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead><tr>
              {["Organisation","Org Admins","Tier","Status","Branches","Tickets Today","Actions"].map(h =>
                <th key={h} style={styles.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.orgName}>{o.name}</div>
                    <div style={styles.orgSlug}>/{o.slug}</div>
                  </td>
                  <td style={styles.td}>
                    {o.org_admins?.length > 0
                      ? o.org_admins.map(a => <div key={a.id} style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{a.name}</div>)
                      : <span style={{ fontSize: "12px", color: "#EF4444" }}>No admin</span>
                    }
                  </td>
                  <td style={styles.td}><Badge value={o.tier} /></td>
                  <td style={styles.td}><Badge value={o.status} /></td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    <span style={{ color: "#60A5FA", fontWeight: "700" }}>{o.branch_count}</span>
                    <span style={{ color: "var(--text-faint)", fontSize: "11px" }}> ({o.active_branch_count} active)</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center", color: "var(--text-secondary)" }}>{o.tickets_today ?? 0}</td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <Btn size="sm" variant="ghost" onClick={() => setManageAdminsModal(o)} title="Manage org admins">Admins</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => setEditModal(o)}>Edit</Btn>
                      {o.status === "suspended"
                        ? <Btn size="sm" variant="success" onClick={() => setConfirm({ type: "activate", org: o })}>Activate</Btn>
                        : <Btn size="sm" variant="warning" onClick={() => setConfirm({ type: "suspend",  org: o })}>Suspend</Btn>
                      }
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateOrgModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(data) => { setShowCreate(false); setResult(data); load(); }}
      />

      <EditOrgModal
        open={!!editModal}
        org={editModal}
        onClose={() => setEditModal(null)}
        onSaved={() => { setEditModal(null); load(); }}
      />

      <Modal open={!!result} onClose={() => setResult(null)} title="Organisation Created 🎉" width="480px">
        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: "12px", padding: "16px", display: "flex", gap: "12px", alignItems: "center" }}>
              <span style={{ fontSize: "28px" }}>🏛️</span>
              <div>
                <div style={{ fontSize: "15px", fontWeight: "700", color: "#22C55E" }}>{result.name}</div>
                {result.branch_count !== undefined && <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>0 branches — add them from the Org Admin portal</div>}
              </div>
            </div>
            {result.temp_password && <TempPasswordBox password={result.temp_password} email={result.admin_user?.email} />}
            <div style={{ display: "flex", justifyContent: "flex-end" }}><Btn onClick={() => setResult(null)}>Done</Btn></div>
          </div>
        )}
      </Modal>

      <ManageOrgAdminsModal
        open={!!manageAdminsModal}
        org={manageAdminsModal}
        onClose={() => setManageAdminsModal(null)}
        onUpdated={() => { load(); }}
      />

      <ConfirmModal
        open={!!confirm} onClose={() => setConfirm(null)}
        onConfirm={handleAction} loading={actionLoad}
        title={confirm?.type === "suspend" ? "Suspend Organisation?" : "Activate Organisation?"}
        message={confirm?.type === "suspend"
          ? `${confirm?.org?.name} and ALL its branches will be suspended.`
          : `${confirm?.org?.name} and its branches will be reactivated.`}
        confirmLabel={confirm?.type === "suspend" ? "Suspend All" : "Activate"}
        variant={confirm?.type === "suspend" ? "danger" : "success"}
      />
    </div>
  );
}

function CreateOrgModal({ open, onClose, onCreated }) {
  const [form, setForm]   = useState({ name:"",slug:"",email:"",phone:"",tier:"starter",status:"trial",admin_name:"",admin_email:"",admin_password:"",send_invite:true,pw_mode:"auto" });
  const [saving, setSaving] = useState(false);
  const [step, setStep]   = useState(0);
  const set  = (k,v) => setForm(p => ({ ...p, [k]: v }));
  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");

  useEffect(() => { if (open) { setForm({ name:"",slug:"",email:"",phone:"",tier:"starter",status:"trial",admin_name:"",admin_email:"",admin_password:"",send_invite:true,pw_mode:"auto" }); setStep(0); } }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await superAdmin.createOrg({
        name: form.name, slug: form.slug, email: form.email,
        phone: form.phone, tier: form.tier, status: form.status,
        admin_name:     form.admin_name  || undefined,
        admin_email:    form.admin_email || undefined,
        admin_password: form.pw_mode === "manual" ? form.admin_password : undefined,
        send_invite:    form.send_invite,
      });
      toast.success(`${form.name} created!`);
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.slug?.[0] || e.response?.data?.detail || "Failed to create organisation.");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create New Organisation" width="540px">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {step === 0 && <>
          <FormField label="Organisation Name" required>
            <input style={inputStyle} value={form.name} onChange={e => { set("name",e.target.value); if(!form.slug) set("slug",slug(e.target.value)); }} placeholder="e.g. Ecobank Ghana" />
          </FormField>
          <FormField label="Slug" required hint={`Org identifier — used internally`}>
            <input style={inputStyle} value={form.slug} onChange={e => set("slug",slug(e.target.value))} />
          </FormField>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
            <FormField label="Tier">
              <select style={selectStyle} value={form.tier} onChange={e => set("tier",e.target.value)}>
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </FormField>
            <FormField label="Status">
              <select style={selectStyle} value={form.status} onChange={e => set("status",e.target.value)}>
                <option value="trial">Trial</option>
                <option value="active">Active</option>
              </select>
            </FormField>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
            <FormField label="Email"><input style={inputStyle} type="email" value={form.email} onChange={e => set("email",e.target.value)} /></FormField>
            <FormField label="Phone"><input style={inputStyle} value={form.phone} onChange={e => set("phone",e.target.value)} /></FormField>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", gap:"10px", paddingTop:"8px", borderTop:"1px solid var(--surface-border)" }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn onClick={() => setStep(1)} disabled={!form.name.trim()||!form.slug.trim()}>Next: Org Admin →</Btn>
          </div>
        </>}
        {step === 1 && <>
          <p style={{ fontSize:"13px", color:"var(--text-secondary)", background:"rgba(37,99,235,0.06)", borderRadius:"8px", padding:"10px 12px", margin:0 }}>
            Add the first org admin. You can add more later. Only Super Admin can create org admins.
          </p>
          <FormField label="Admin Full Name"><input style={inputStyle} value={form.admin_name} onChange={e => set("admin_name",e.target.value)} placeholder="e.g. Kofi Ansa" /></FormField>
          <FormField label="Admin Email"><input style={inputStyle} type="email" value={form.admin_email} onChange={e => set("admin_email",e.target.value)} placeholder="admin@organisation.com" /></FormField>
          {form.admin_email && (
            <FormField label="Password">
              <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
                <label style={{ display:"flex", gap:"8px", cursor:"pointer", fontSize:"13px", color:"var(--text-primary)" }}><input type="radio" checked={form.pw_mode==="auto"} onChange={() => set("pw_mode","auto")} />Auto-generate</label>
                <label style={{ display:"flex", gap:"8px", cursor:"pointer", fontSize:"13px", color:"var(--text-primary)" }}><input type="radio" checked={form.pw_mode==="manual"} onChange={() => set("pw_mode","manual")} />Set manually</label>
              </div>
            </FormField>
          )}
          {form.admin_email && form.pw_mode==="manual" && (
            <FormField label="Password"><input style={inputStyle} type="password" value={form.admin_password} onChange={e => set("admin_password",e.target.value)} placeholder="Min. 8 characters" /></FormField>
          )}
          {form.admin_email && (
            <label style={{ display:"flex", gap:"8px", cursor:"pointer", fontSize:"13px", color:"var(--text-secondary)" }}>
              <input type="checkbox" checked={form.send_invite} onChange={e => set("send_invite",e.target.checked)} />
              Send welcome email with credentials
            </label>
          )}
          <div style={{ display:"flex", justifyContent:"flex-end", gap:"10px", paddingTop:"8px", borderTop:"1px solid var(--surface-border)" }}>
            <Btn variant="ghost" onClick={() => setStep(0)}>← Back</Btn>
            <Btn onClick={submit} disabled={saving}>{saving ? "Creating…" : "✓ Create Organisation"}</Btn>
          </div>
        </>}
      </div>
    </Modal>
  );
}

function EditOrgModal({ open, org, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && org) setForm({ name: org.name, email: org.email||"", phone: org.phone||"", tier: org.tier, status: org.status }); }, [open, org]);
  const set = (k,v) => setForm(p => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await superAdmin.updateOrg(org.id, form);
      toast.success(`${form.name} updated.`);
      onSaved();
    } catch { toast.error("Failed to update."); }
    finally  { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Edit Organisation" width="480px">
      <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>
        <FormField label="Name" required><input style={inputStyle} value={form.name||""} onChange={e => set("name",e.target.value)} /></FormField>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
          <FormField label="Email"><input style={inputStyle} type="email" value={form.email||""} onChange={e => set("email",e.target.value)} /></FormField>
          <FormField label="Phone"><input style={inputStyle} value={form.phone||""} onChange={e => set("phone",e.target.value)} /></FormField>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px" }}>
          <FormField label="Tier"><select style={selectStyle} value={form.tier||"starter"} onChange={e => set("tier",e.target.value)}><option value="starter">Starter</option><option value="growth">Growth</option><option value="enterprise">Enterprise</option></select></FormField>
          <FormField label="Status"><select style={selectStyle} value={form.status||"trial"} onChange={e => set("status",e.target.value)}><option value="trial">Trial</option><option value="active">Active</option><option value="suspended">Suspended</option></select></FormField>
        </div>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:"10px", paddingTop:"8px", borderTop:"1px solid var(--surface-border)" }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={save} disabled={saving||!form.name?.trim()}>{saving ? "Saving…" : "Save Changes"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root:     { padding:"32px", fontFamily:"'DM Sans','Segoe UI',sans-serif", maxWidth:"1200px" },
  toolbar:  { marginBottom:"20px" },
  search:   { background:"var(--input-bg)", border:"1px solid var(--input-border)", borderRadius:"8px", padding:"9px 14px", color:"var(--text-primary)", fontSize:"14px", outline:"none", width:"280px", fontFamily:"inherit" },
  tableWrap:{ background:"var(--surface-bg)", border:"1px solid var(--surface-border)", borderRadius:"14px", overflow:"hidden", boxShadow: "var(--card-shadow)" },
  table:    { width:"100%", borderCollapse:"collapse" },
  th:       { padding:"12px 16px", fontSize:"11px", fontWeight:"700", color:"var(--text-muted)", letterSpacing:"0.07em", textTransform:"uppercase", textAlign:"left", borderBottom:"1px solid var(--surface-border)", background:"var(--table-header-bg)" },
  tr:       { borderBottom:"1px solid var(--surface-border)" },
  td:       { padding:"13px 16px", verticalAlign:"middle" },
  orgName:  { fontSize:"14px", fontWeight:"600", color:"var(--text-primary)" },
  orgSlug:  { fontSize:"11px", color:"var(--text-faint)", fontFamily:"monospace" },
};
